import type { ColumnDef } from '@tanstack/react-table'
import type { NotificationRecipient } from '@/lib/services/notification-recipients/notification-recipients.types'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { NotificationChannelType } from '@/lib/enums/NotificationChannelType'
import { getNotificationChannelLabel } from '@/lib/notification-channel-labels'
import { useUpdateNotificationRecipientMutation } from '@/lib/services/notification-recipients/notification-recipients.query'
import { getErrorMessage } from '@/lib/utils'
import { Phone, Mail, Printer, Check, AlertTriangle, MessageSquare } from 'lucide-react'
import { DataTableColumnHeader } from '../DataTableColumnHeader'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { toast } from 'sonner'
import { useAbility } from '@/lib/casl'

const getTypeIcon = (channel: string) => {
  switch (channel) {
    case NotificationChannelType.Text.Value:
      return MessageSquare
    case NotificationChannelType.Phone.Value:
      return Phone
    case NotificationChannelType.Email.Value:
      return Mail
    case NotificationChannelType.Fax.Value:
      return Printer
    default:
      return AlertTriangle
  }
}

const channelTypeByValue = Object.fromEntries(
  Object.values(NotificationChannelType).map((channelType) => [channelType.Value, channelType])
)

const getTypeLabel = (channel: string): string => {
  const channelType = channelTypeByValue[channel]

  return getNotificationChannelLabel(channel, channelType?.Name ?? channel)
}

export const notificationRecipientColumns: ColumnDef<NotificationRecipient>[] = [
  {
    id: 'recipient',
    accessorKey: 'value',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Recipient" />,
    enableSorting: false,
    meta: {
      label: 'Recipient',
      kind: 'primary',
      headClassName: 'w-full',
      cellClassName: 'w-full',
    },
    cell: function CellComponent({ row }) {
      const recipient = row.original
      const Icon = getTypeIcon(recipient.channel)
      const { formatPhone } = usePhoneFormatter()

      const phoneChannels: string[] = [
        NotificationChannelType.Text.Value,
        NotificationChannelType.Phone.Value,
        NotificationChannelType.Fax.Value,
      ]
      const displayValue = phoneChannels.includes(recipient.channel)
        ? formatPhone(recipient.value)
        : recipient.value

      return (
        <div className="flex items-center space-x-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="p-2 rounded-lg bg-muted">
                  <Icon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getTypeLabel(recipient.channel)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="flex flex-col flex-1">
            <div className="font-medium">{recipient.label || displayValue}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              <span className="">{displayValue}</span>
            </div>
          </div>
        </div>
      )
    },
  },
  {
    id: 'status',
    accessorFn: (recipient) => (recipient.verified_at !== null ? 1 : 0),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" className="justify-center" />
    ),
    enableSorting: true,
    meta: {
      label: 'Status',
      kind: 'badge',
      align: 'center',
      headClassName: 'w-[72px]',
      cellClassName: 'w-[72px]',
    },
    cell: ({ row }) => {
      const recipient = row.original
      const isVerified = recipient.verified_at !== null

      let icon, tooltipText

      if (isVerified) {
        icon = <Check className="text-status-success-foreground" />
        tooltipText = 'Verified'
      } else {
        icon = <AlertTriangle className="text-status-warning-foreground" />
        tooltipText = 'Not Verified'
      }

      return (
        <div className="flex justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>{icon}</TooltipTrigger>
              <TooltipContent>
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )
    },
  },
  {
    accessorKey: 'enabled',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Enabled" className="justify-center" />
    ),
    enableSorting: true,
    meta: {
      label: 'Enabled',
      kind: 'badge',
      align: 'center',
      headClassName: 'w-[92px]',
      cellClassName: 'w-[92px]',
    },
    cell: function EnabledCell({ row }) {
      const recipient = row.original
      const ability = useAbility()
      const updateMutation = useUpdateNotificationRecipientMutation()
      const canEdit = ability.can('edit', 'notification-recipients')
      const isEnabled =
        updateMutation.isPending && updateMutation.variables?.id === recipient.id
          ? updateMutation.variables.enabled
          : recipient.enabled
      const recipientName = recipient.label || recipient.value
      const actionLabel = isEnabled ? 'Disable recipient' : 'Enable recipient'

      const handleEnabledChange = (enabled: boolean) => {
        updateMutation.mutate(
          {
            id: recipient.id,
            channel: recipient.channel,
            value: recipient.value,
            label: recipient.label ?? '',
            enabled,
          },
          {
            onSuccess: () => {
              toast.success(`Notification recipient ${enabled ? 'enabled' : 'disabled'}`)
            },
            onError: (error) => {
              toast.error('Failed to update notification recipient', {
                description: getErrorMessage(error),
              })
            },
          }
        )
      }

      return (
        <div className="flex justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <Switch
                    checked={isEnabled}
                    disabled={!canEdit || updateMutation.isPending}
                    aria-label={`${actionLabel}: ${recipientName}`}
                    onCheckedChange={handleEnabledChange}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{canEdit ? actionLabel : 'You do not have permission to edit recipients'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )
    },
  },
  {
    id: 'channel_type',
    accessorKey: 'channel',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Channel" className="text-center" />
    ),
    enableSorting: true,
    meta: {
      label: 'Channel',
      kind: 'badge',
      align: 'center',
      headClassName: 'w-[120px] text-center',
      cellClassName: 'w-[120px]',
    },
    cell: ({ row }) => {
      return (
        <div className="flex w-[112px] justify-center">
          <Badge variant="secondary">{getTypeLabel(row.original.channel)}</Badge>
        </div>
      )
    },
  },
]
